"use client";

import { clientErrorTracker } from "@repo/error-tracking/client";
import { MessageSquareWarning, RefreshCw } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";

// The root layout (and its stylesheet) is replaced when this renders, so
// styles are inline.
export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	const [reference, setReference] = useState<string>();
	const [feedbackAvailable, setFeedbackAvailable] = useState(false);

	useEffect(() => {
		clientErrorTracker
			.captureError(error, {
				type: "render",
				severity: "critical",
				context: { extra: { globalError: true, digest: error.digest } },
			})
			.then((captured) => {
				setReference(captured?.id);
				setFeedbackAvailable(clientErrorTracker.isFeedbackAvailable());
			});
	}, [error]);

	async function reportProblem() {
		const opened = await clientErrorTracker.openFeedbackForm(reference);
		if (!opened) {
			setFeedbackAvailable(false);
		}
	}

	return (
		<html lang="en">
			<body style={styles.page}>
				<main style={styles.card}>
					<h1 style={styles.title}>Something went wrong</h1>
					<p style={styles.text}>
						We hit an unexpected problem loading this page. Our team has been notified. Please
						try again.
					</p>
					<button type="button" onClick={reset} style={{ ...styles.button, ...styles.primary }}>
						<RefreshCw size={16} aria-hidden="true" />
						Try again
					</button>
					{feedbackAvailable && (
						<button type="button" onClick={reportProblem} style={styles.button}>
							<MessageSquareWarning size={16} aria-hidden="true" />
							Report a problem
						</button>
					)}
					{reference && (
						<p style={styles.reference}>
							Reference: <span style={{ fontFamily: "monospace" }}>{reference}</span>
						</p>
					)}
				</main>
			</body>
		</html>
	);
}

const styles = {
	page: {
		margin: 0,
		minHeight: "100vh",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 16,
		background: "#f1f5f9",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#0f172a",
	},
	card: {
		width: "100%",
		maxWidth: 420,
		background: "#fff",
		borderRadius: 16,
		padding: 32,
		textAlign: "center",
		boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
	},
	title: { fontSize: 24, margin: "0 0 12px" },
	text: { color: "#475569", lineHeight: 1.5, margin: "0 0 24px" },
	button: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		width: "100%",
		minHeight: 44,
		marginTop: 12,
		borderRadius: 12,
		border: "1px solid #cbd5e1",
		background: "#fff",
		color: "#0f172a",
		fontSize: 15,
		fontWeight: 600,
		cursor: "pointer",
	},
	primary: { background: "#2563eb", borderColor: "#2563eb", color: "#fff" },
	reference: { fontSize: 12, color: "#94a3b8", marginTop: 20 },
} satisfies Record<string, CSSProperties>;
